/**
 * useLinearityData.js
 *
 * Custom hooks for fetching linearity/projection data.
 * Provides quarterly and monthly variants with identical return interfaces.
 *
 * Quarterly data uses Redux for caching (shared with other components).
 * Monthly data uses local state (self-contained, no Redux changes needed).
 *
 * Usage:
 * - const { chartData, appliedFilters, isLoading } = useQuarterlyLinearityData();
 * - const { chartData, appliedFilters, isLoading } = useMonthlyLinearityData();
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { appService } from "../../App/app.service";
import { transformLinearityData, buildDefaultFilters } from "../RccCards/linearityUtils";

/**
 * Hook for fetching quarterly linearity comparison data.
 * Uses Redux for caching since quarterly data is shared across components.
 *
 * @returns {Object} { chartData, appliedFilters, isLoading, error }
 */
export function useQuarterlyLinearityData() {
  const dispatch = useDispatch();
  const fetchingRef = useRef(false);

  // Use existing Redux state for quarterly data (funnelData)
  // This allows data sharing between LinearityComparisonCard and other components
  const funnelData = useSelector((state) => state.app.funnelData || "");
  const updatingFunnelData = useSelector(
    (state) => state.app.updatingFunnelData || false
  );

  const [appliedFilters, setAppliedFilters] = useState([]);
  const isLoading = funnelData === "" || updatingFunnelData;

  useEffect(() => {
    // Skip if already fetching, if data is already loaded, or if update is in progress
    if (fetchingRef.current || funnelData !== "" || updatingFunnelData) return;

    fetchingRef.current = true;
    dispatch({ type: "get_funnel_filters_request" });

    appService
      .getTimeSeriesPredictionv2Filters()
      .then((resp) => {
        dispatch({
          type: "get_funnel_filters_success",
          json: resp,
        });

        dispatch({ type: "get_funnel_data_request" });
        dispatch({ type: "get_generic_pipeline_coverage_data_CFQ_request" });

        // Build filters using shared utility
        const { filters } = buildDefaultFilters(resp, {
          periodType: "quarter",
          lookbackPeriods: 4,
        });

        setAppliedFilters(filters);
        dispatch({ type: "current_selected_filters", filters });

        // Fetch funnel data
        return appService.getTimeSeriesPredictionv2Data(filters);
      })
      .then((dataResp) => {
        dispatch({
          type: "get_funnel_data_success",
          json: dataResp,
        });
        fetchingRef.current = false;
      })
      .catch((error) => {
        dispatch({
          type: "get_funnel_data_failure",
          error: typeof error === "object" ? "Something went wrong" : error,
        });
        fetchingRef.current = false;
      });
  }, [funnelData, updatingFunnelData, dispatch]);

  // Transform data for chart using shared utility
  const chartData = useMemo(() => {
    if (!funnelData || funnelData === "") return [];

    const linearityData = funnelData.linearity || [];
    const cfq = funnelData.cfq || funnelData.currentFiscalQuarter;

    return transformLinearityData(linearityData, cfq);
  }, [funnelData]);

  return { chartData, appliedFilters, isLoading, error: null };
}

/**
 * Hook for fetching monthly linearity comparison data.
 * Uses local state instead of Redux (self-contained, no shared state).
 *
 * Similar interface to useQuarterlyLinearityData but uses monthly APIs:
 * - getTimeSeriesPredictionByMonthFilters()
 * - getTimeSeriesPredictionByMonthData()
 *
 * @returns {Object} { chartData, appliedFilters, isLoading, error }
 */
export function useMonthlyLinearityData() {
  const fetchingRef = useRef(false);

  // Local state for monthly data (no Redux)
  const [monthlyData, setMonthlyData] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Skip if already fetching
    if (fetchingRef.current) return;

    fetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    appService
      .getTimeSeriesPredictionByMonthFilters()
      .then((resp) => {
        // Build filters using shared utility with monthly period type
        const { filters } = buildDefaultFilters(resp, {
          periodType: "month",
          lookbackPeriods: 4,
        });

        setAppliedFilters(filters);

        // Fetch monthly data
        return appService.getTimeSeriesPredictionByMonthData(filters);
      })
      .then((dataResp) => {
        // Store the response message (contains linearity array and cfm)
        setMonthlyData(dataResp.message || dataResp);
        setIsLoading(false);
        fetchingRef.current = false;
      })
      .catch((err) => {
        console.error("Error fetching monthly linearity data:", err);
        const errorMsg =
          err && typeof err === "object"
            ? err.message || err.error || "Something went wrong"
            : err || "Something went wrong";
        setError(errorMsg);
        setIsLoading(false);
        fetchingRef.current = false;
      });
  }, []);

  // Transform data for chart using shared utility
  const chartData = useMemo(() => {
    if (!monthlyData) return [];

    const linearityData = monthlyData.linearity || [];
    // Monthly API uses "cfm" (current fiscal month) instead of "cfq"
    const currentPeriod =
      monthlyData.cfm || monthlyData.currentFiscalMonth || monthlyData.cfq;

    return transformLinearityData(linearityData, currentPeriod);
  }, [monthlyData]);

  return { chartData, appliedFilters, isLoading, error };
}
